import React, { FC, ReactElement, FormEvent, useState, useEffect, ChangeEvent } from 'react';
import cns from 'classnames';
import './RegisterPage.sass';
import ReactHtmlParser from 'react-html-parser';
import { useDispatch, useSelector } from 'react-redux';
import {
  getContentDataIntro,
  getEventKey,
  setUserEmail,
  setUserRegistrationFields,
  setIsRegisterInProgress,
} from '../../../stores/reducers/userData';
import { RegistrationProps } from './types';
import { checkRegistrationByEmail, registerByEmail } from '../../../api';
import LoginModal, { PASSWORD_RESTORE_TIME } from '../../Modals/LoginModal/LoginModal';
import FormFields from './FormFields';
import localeFieldResolver from '../../../helpers/locale-field-finder';
import { internalizationState } from '../../../stores/reducers/sections';
import responseErrorChecker from 'src/helpers/response-error-checker';
import {storageFactory} from '../../../helpers/storage';

const local = storageFactory(() => localStorage);

const RegisterPage: FC<RegistrationProps> = ({
  className,
  bgSettings,
  // guestInfo,
  registrationFields,
  eventRules,
  sectionName,
  sectionId,
  allowedEmailDomain,
  onDomainError,
  sectionData
}): ReactElement => {
  const dispatch = useDispatch();
  // const history = useHistory();
  const eventKey = useSelector(getEventKey);
  const contentData = useSelector(getContentDataIntro);
  const [inputValues, setInputValues] = useState<any>({});
  const [isUserAgree, setIsUserAgree] = useState(false);
  const [loginModalData, setLoginModalData] = useState({ isOpen: false, title: '', notice: '' });
  const { activeLangState } = useSelector(internalizationState);
  const localizeData = {
    registration_ru: 'Регистрация',
    registration_en: 'Registration',
    onDomainError_ru: 'Организатор ограничил доступ к мероприятию для данного доменного имени почты.',
    onDomainError_en: 'The organizer has restricted access to the event for this mail domain name.',
    registrationConfirm_ru: 'Поздравляем, вы успешно зарегистрированы на мероприятие!',
    registrationConfirm_en: 'Congratulations, you have successfully registered for the event!',
    registrationConfirmNotice_ru: `Мы направили вам на почту ${inputValues.email} письмо о подтверждении регистрации и одноразовый код для доступа на платформу. Вы можете воспользоваться им сейчас и перейти к мероприятию или сделать это позже в любой удобный момент, перейдя по ссылки из письма о регистрации. Если вы не можете найти наши письма, проверьте папку Спам или свяжитесь с поддержкой support@comdi.com.`,
    registrationConfirmNotice_en: `We have sent you a registration confirmation email and a one-time code to access the platform by email ${inputValues.email}. You can use it now and proceed to the event, or do it later at any time by following the link in the registration email. If you cannot find our emails, please check your Spam folder or contact support@comdi.com.`,
    login_ru: 'Войти',
    login_en: 'Login',
    loginNotice_ru: `Ваша почта ${inputValues.email} уже зарегистрирована на мероприятие "${localeFieldResolver(contentData, 'eventName', activeLangState)}". Мы отправили вам новый одноразовый код на почту. Введите его для перехода к мероприятию.`,
    loginNotice_en: `Your email ${inputValues.email} is already registered for the event "${localeFieldResolver(contentData, 'eventName', activeLangState)}". We have sent you a new one-time code by email. Enter it to go to the event.`,
    onlyNumbersAndLetters_ru: 'Только буквы и цифры',
    onlyNumbersAndLetters_en: 'Letters and numbers only',
    back_ru: 'Назад',
    back_en: 'Back',
    acceptConditionsShort_ru: 'Я принимаю все вышеперечисленные условия',
    acceptConditionsShort_en: 'I accept all of the above conditions',
    acceptConditions_ru: `
      Передавая свои данные и становясь участником мероприятия, я принимаю условия&nbsp;
      <a href="https://www.comdi.com/legal/user-agreement.pdf">Пользовательского соглашения</a>,
      <a href="https://www.comdi.com/legal/private-agreement-services.pdf">
        &nbsp;соглашаюсь на обработку персональных данных
      </a>
      &nbsp;и связь со мной способами, указанными в Пользовательском соглашении, в целях исполнения
      условий Пользовательского соглашения.
    `,
    acceptConditions_en: `
      By transferring my data and becoming a participant in the event, I accept the terms&nbsp;
      <a href="https://www.comdi.com/legal/user-agreement.pdf">of the User Agreement</a>,
      <a href="https://www.comdi.com/legal/private-agreement-services.pdf">
        &nbsp;I agree to the processing of personal data
      </a>
      &nbsp;and communication with me in the ways specified in the User Agreement for the purpose of execution
      terms of the User Agreement.
    `,
  };

  const localizedName = (field: string): string => localeFieldResolver(localizeData, field, activeLangState);
  
  // useEffect(() => {
  //   if (guestInfo && guestInfo.email) dispatch(setUserEmail(guestInfo.email));
  //   window.dispatchEvent((window as any).regPageOpen);
  // }, [guestInfo, dispatch]);

  const checkRegistrationEmail = (email: string): boolean => {
    let domains: string[] = []
    const userDomain = email.split('@')[1];

    if (allowedEmailDomain && allowedEmailDomain.length) {
      domains = allowedEmailDomain.split(/[\s;,]+/);
      if (domains.includes(userDomain)) {
        return true;
      }
      return false;
    }

    return true;
  };

  const saveEventRegistrationFields = () => {
    if (registrationFields) {
      const fields: any = {};
      registrationFields.forEach((field) => {
        fields[field.field.name] = inputValues[field.field.name];
      });
      dispatch(setUserRegistrationFields(fields));
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      window.dispatchEvent((window as any).regFinished);
      saveEventRegistrationFields();
      // const name = guestInfo?.name || inputValues.name;
      // const surname = guestInfo?.surname || inputValues.surname;
      // const email = guestInfo?.email || inputValues.email;
      // const patronymic = guestInfo?.patronymic || inputValues.patronymic || '';
      if (inputValues.email) {
        if (!checkRegistrationEmail(inputValues.email)) {
          onDomainError(localizedName('onDomainError_ru'));
          return;
        }

        dispatch(setUserEmail(inputValues.email));

        const checkRegistrationByEmailResponse = await checkRegistrationByEmail(inputValues.email, eventKey) as Response;
        const checkRegistrationByEmailPermission = await responseErrorChecker(checkRegistrationByEmailResponse, activeLangState);
        if (!checkRegistrationByEmailPermission) return;

        const checkRegistrationByEmailData = await (checkRegistrationByEmailResponse).json();

        if (checkRegistrationByEmailData && Object.keys(checkRegistrationByEmailData).length) {

          const { status } = checkRegistrationByEmailData;
          dispatch(setIsRegisterInProgress(true));
          if (!status) {
            const registerByEmailResponse = await registerByEmail({...inputValues, termsAgree: isUserAgree}, eventKey) as Response;
            const registerByEmailPermission = await responseErrorChecker(registerByEmailResponse, activeLangState);

            if (!registerByEmailPermission) return;

            const registerByEmailData = await registerByEmailResponse.json();

            if (registerByEmailData && registerByEmailData.status) {
              local.setItem(PASSWORD_RESTORE_TIME, registerByEmailData.passwordLockTime.toString());

              setLoginModalData({
                isOpen: true,
                title: localizedName('registrationConfirm_ru'),
                notice: localizedName('registrationConfirmNotice_ru'),
              });
            }
          } else {
            if (checkRegistrationByEmailData.status && checkRegistrationByEmailData.passwordUpdateLock) {
              local.setItem(PASSWORD_RESTORE_TIME, checkRegistrationByEmailData.passwordUpdateLock.toString());
            }

            setLoginModalData({
              isOpen: true,
              title: localizedName('login_ru'),
              notice: localizedName('loginNotice_ru'),
            });
          }
        } else {
          console.error('checkRegistrationByEmail: bad response!');
        }

      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleFieldChange = (
    e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>,
    name: string | undefined
  ) => {
    const { target } = e;
    if (target.validity.patternMismatch) {
      target.setCustomValidity(localizedName('onlyNumbersAndLetters_ru'));
      window.scrollTo(0, 0);
    } else {
      target.setCustomValidity('');
    }

    if (name) {
      const value = target.type !== 'checkbox' ? target.value : (target as {checked: boolean}).checked;
      setInputValues({
        ...inputValues,
        [name]: value,
      });
    }
  };

  const getStyleParam = (type: string): string => {
    return sectionData.find((param) => param.type === type)?.value ?? ''
  }

  return (
    <>
      {loginModalData.isOpen && (
        <LoginModal
          isOpen={loginModalData.isOpen}
          title={loginModalData.title}
          notice={loginModalData.notice}
          goal="enterTheCode"
          isConfirmRegistrationMode
          closeModal={() => setLoginModalData({ ...loginModalData, isOpen: false })}
        />
      )}
      <section
        className={cns(
          'static-page-section register',
          // bgSettings && bgSettings.isDark && 'inner-section--dark-bg',
          className
        )}
        style={{ backgroundColor: bgSettings?.color }}
        id={sectionId}
      >
        <div className="inner-section__head">
          <h1 style={{color: getStyleParam('titleColor')}}>
            {sectionName}
          </h1>
        </div>
        <div className="container">
          <div style={{ padding: '14px 0' }} />
          <form className="registration-form" onSubmit={(e) => handleSubmit(e)}>
            <FormFields
              registrationFields={registrationFields}
              // guestInfo={guestInfo}
              handleFieldChange={handleFieldChange}
              inputValues={inputValues}
            />
            <div className="wrapper-agreement">
              <div className="agreement-text">
                {
                  eventRules && eventRules.rules === true ?
                    ReactHtmlParser(localizedName('acceptConditions_ru'))
                  :
                    <></>
                }
                <br />
                <br />
                { eventRules && ReactHtmlParser(localeFieldResolver(eventRules, 'customRulesRu', activeLangState)) }
                {/* {(eventRules?.customRulesRu || eventRules?.customRulesEn) ? (
                  ReactHtmlParser(localeFieldResolver(eventRules, 'customRulesRu', activeLangState))
                ) : (
                  ReactHtmlParser(localizedName('acceptConditions_ru'))
                )} */}
              </div>
              { eventRules && (eventRules.rules === true || eventRules?.customRulesRu || eventRules?.customRulesEn) ?
                  <label>
                    <span className="agreement-text">{ localizedName('acceptConditionsShort_ru') }</span>
                    <input type="checkbox" checked={isUserAgree} onChange={() => setIsUserAgree(!isUserAgree)} />
                    <span className="checkmark" />
                  </label>
                :
                  <></>
              }
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!isUserAgree}
              style={{
                backgroundColor: isUserAgree ? getStyleParam('buttonColor') : '',
                color: isUserAgree ? getStyleParam('buttonFontColor') : ''
              }}
            >
              { localizedName('registration_ru') }
            </button>
          </form>
        </div>
      </section>
    </>
  );
};

export default RegisterPage;
